import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Case } from '../types';
import apiService from '../services/api';
import CaseCard from '../components/Cases/CaseCard';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  BookOpenIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

const CasesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [masterCases, setMasterCases] = useState<any[]>([]);
  const [showMasterCases, setShowMasterCases] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('ALL');
  const [versionFilter, setVersionFilter] = useState<string>('ALL');
  const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);
  const [deletingMasterId, setDeletingMasterId] = useState<string | null>(null);
  const [isInstructor, setIsInstructor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [userFilter, setUserFilter] = useState<string>('ALL');
  
  // Check user role on mount and when localStorage changes
  useEffect(() => {
    const checkUserRole = () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          console.log('User role from localStorage:', user.role);
          const instructorRole = user.role === 'INSTRUCTOR' || user.role === 'ADMIN';
          const adminRole = user.role === 'ADMIN';
          console.log('Is instructor:', instructorRole);
          console.log('Is admin:', adminRole);
          setIsInstructor(instructorRole);
          setIsAdmin(adminRole);
          
          // Load users list if admin
          if (adminRole) {
            loadUsers();
          }
        } catch (e) {
          console.error('Error parsing user from localStorage:', e);
          setIsInstructor(false);
          setIsAdmin(false);
        }
      } else {
        console.log('No user in localStorage');
        setIsInstructor(false);
        setIsAdmin(false);
      }
    };
    
    checkUserRole();
  }, []);

  useEffect(() => {
    loadCases();
    loadMasterCases();
  }, [userFilter]);

  const loadUsers = async () => {
    try {
      const usersList = await apiService.getAllUsers();
      setUsers(usersList);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  const loadCases = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Load user's case instances (their personal copies)
      // If admin has selected a specific user, filter by that user
      const filterUserId = userFilter !== 'ALL' ? userFilter : undefined;
      const userInstances = await apiService.getUserCaseInstances(filterUserId);
      
      console.log('Loaded user instances:', userInstances);
      
      // Transform instances to match Case type for display
      const transformedCases = userInstances.map((instance: any) => {
        console.log('Transforming instance:', instance.instanceId, 'versions:', instance._count?.versions);
        return {
          id: instance.id,
          caseId: instance.instanceId, // Use instanceId as caseId for routing
          title: instance.title,
          patientProfile: instance.patientProfile,
          scenario: instance.scenario,
          familyStatement: instance.familyStatement,
          clinicianObjectives: instance.clinicianObjectives,
          createdById: instance.userId,
          createdAt: instance.startedAt,
          updatedAt: instance.lastAccessedAt,
          createdBy: instance.user,
          learnerLevel: instance.learnerLevel,
          dialogues: instance.userDialogue ? [instance.userDialogue] : [],
          _count: {
            dialogues: instance.userDialogue ? 1 : 0,
            versions: instance._count?.versions || 0
          }
        };
      });
      
      console.log('Transformed cases:', transformedCases);
      setCases(transformedCases);
    } catch (err: any) {
      console.error('Failed to load cases:', err);
      setError(err.response?.data?.error || 'Failed to load your cases');
      setCases([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const loadMasterCases = async () => {
    try {
      const masters = await apiService.getMasterCases();
      setMasterCases(masters);
    } catch (err: any) {
      console.error('Failed to load master cases:', err);
    }
  };

  const handleStartCase = async (masterCaseId: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('Starting case from master:', masterCaseId);
      const newInstance: any = await apiService.startCaseFromMaster(masterCaseId);
      
      console.log('Started instance:', newInstance);
      
      // Reload all cases to get fresh data
      await loadCases();
      setShowMasterCases(false);
    } catch (err: any) {
      console.error('Failed to start case:', err);
      setError(err.response?.data?.error || 'Failed to start case');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUserCase = async (instanceId: string, title: string) => {
    // Set deleting state to show inline confirmation
    setDeletingCaseId(instanceId);
  };

  const confirmDeleteUserCase = async (instanceId: string) => {
    try {
      setError('');
      await apiService.deleteUserCaseInstance(instanceId);
      setDeletingCaseId(null);
      await loadCases(); // Reload list
    } catch (err: any) {
      console.error('Failed to delete case:', err);
      setError(err.response?.data?.error || 'Failed to delete case');
      setDeletingCaseId(null);
    }
  };

  const cancelDeleteUserCase = () => {
    setDeletingCaseId(null);
  };

  const handleDeleteMasterCase = async (caseId: string, title: string) => {
    // Set deleting state to show inline confirmation
    setDeletingMasterId(caseId);
  };

  const confirmDeleteMasterCase = async (caseId: string) => {
    try {
      setError('');
      await apiService.deleteMasterCase(caseId);
      setDeletingMasterId(null);
      await loadMasterCases(); // Reload master cases
    } catch (err: any) {
      console.error('Failed to delete master case:', err);
      setError(err.response?.data?.error || 'Failed to delete template');
      setDeletingMasterId(null);
    }
  };

  const cancelDeleteMasterCase = () => {
    setDeletingMasterId(null);
  };

  const filteredCases = cases
    .filter(c => 
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.scenario.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(c => 
      levelFilter === 'ALL' || 
      (levelFilter === '' ? !c.learnerLevel : c.learnerLevel === levelFilter)
    )
    .filter(c => {
      if (versionFilter === 'ALL') return true;
      const versionCount = c._count?.versions || 0;
      if (versionFilter === 'UPDATED') return versionCount > 1; // More than initial version
      if (versionFilter === 'UNTOUCHED') return versionCount <= 1; // Only initial version or no versions
      return true;
    });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isAdmin && userFilter !== 'ALL' 
                ? `Cases: ${users.find(u => u.id === userFilter)?.name || 'User'}`
                : 'My Cases'}
            </h1>
            <p className="mt-2 text-gray-600">
              {isAdmin && userFilter !== 'ALL'
                ? `Viewing case instances for ${users.find(u => u.id === userFilter)?.email || 'selected user'}`
                : 'Your personal case instances - practice and track your progress'}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowMasterCases(!showMasterCases)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <BookOpenIcon className="h-4 w-4 mr-2" />
              {showMasterCases ? 'Hide Templates' : 'Browse Templates'}
            </button>
            <button
              onClick={() => navigate('/cases/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Custom Case
            </button>
          </div>
        </div>
      </div>

      {/* Master Cases Modal */}
      {showMasterCases && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Templates</h2>
          <p className="text-sm text-gray-600 mb-4">
            Start a case from these templates. You'll get your own copy to practice with.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {masterCases.map((master) => (
              <div key={master.id} className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{master.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{master.scenario}</p>
                
                {deletingMasterId === master.id ? (
                  // Inline confirmation UI
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-2">
                    <p className="text-sm text-red-800 mb-3">
                      <strong>Delete this template?</strong><br/>
                      This will remove it for all users. This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => confirmDeleteMasterCase(master.id)}
                        className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                      >
                        Yes, Delete
                      </button>
                      <button
                        onClick={cancelDeleteMasterCase}
                        className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStartCase(master.caseId)}
                      disabled={isLoading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      Start This Case
                    </button>
                    {isInstructor && (
                      <button
                        onClick={() => handleDeleteMasterCase(master.id, master.title)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search your cases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Admin User Filter */}
      {isAdmin && (
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-purple-800 mb-2">
            Admin: Filter by User
          </label>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-purple-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm bg-white"
          >
            <option value="ALL">All Users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email}) - {user.role}
              </option>
            ))}
          </select>
          {userFilter !== 'ALL' && (
            <p className="mt-2 text-sm text-purple-600">
              Showing cases for: {users.find(u => u.id === userFilter)?.name || 'Unknown'}
            </p>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Learner Level Filter */}
        <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Learner Level
        </label>
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option value="ALL">All Levels</option>
          <option value="BEGINNER">🌱 Beginner</option>
          <option value="INTERMEDIATE">📚 Intermediate</option>
          <option value="ADVANCED">🎓 Advanced</option>
          <option value="">No Level Set</option>
        </select>
        </div>

        {/* Version History Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Activity Status
          </label>
          <select
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="ALL">All Cases</option>
            <option value="UPDATED">📝 Updated (has version history)</option>
            <option value="UNTOUCHED">🆕 Untouched (no changes yet)</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading your cases...</span>
        </div>
      )}

      {/* Cases Grid */}
      {!isLoading && (
        <>
          {!filteredCases || filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No cases found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search.' : 'Start a case from templates or generate a new AI case.'}
              </p>
              <div className="mt-6 flex justify-center space-x-3">
                <button
                  onClick={() => setShowMasterCases(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <BookOpenIcon className="h-4 w-4 mr-2" />
                  Browse Templates
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map((caseData) => (
                <CaseCard 
                  key={caseData.id} 
                  caseData={caseData} 
                  onDelete={handleDeleteUserCase}
                  isDeleting={deletingCaseId === caseData.caseId}
                  onConfirmDelete={confirmDeleteUserCase}
                  onCancelDelete={cancelDeleteUserCase}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CasesPage;
